
import React from "react";
import { useEffect, useMemo, useState } from "react";
import {
    ArrowRight,
    CalendarClock,
    DollarSign,
    Dumbbell,
    Percent,
    RefreshCcw,
    Search,
    TrendingUp,
    Trophy,
    UserCheck,
    UserPlus,
    UserX,
    Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRenewStats } from "../services/renewService";
import { isSuperAdmin } from "../utils/permissions";

type PackageMixRow = {
    id: string;
    name: string;
    soldCount: number;
    revenue: number;
};

type MonthlyTrendRow = {
    month: number;
    label: string;
    totalSubscriptions: number;
};

type CohortTrendRow = {
    month: number;
    label: string;
    newClients: number;
    returningClients: number;
    totalClients: number;
};

type FollowUpMemberRow = {
    id: string;
    memberName: string;
    packageName: string;
    expirationDate: string;
    daysOverdue: number;
    mobile: string;
    email: string;
};

type RenewalConversion = {
    dueMembers: number;
    renewedMembers: number;
    notRenewedMembers: number;
    conversionRate: number;
};

type AnalyticsView = "churn" | "conversion" | "cohorts" | "inactive";

const DAYS_WINDOW = 5;

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const asNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
};

const readFirstNumber = (source: Record<string, unknown>, keys: string[], fallback: number): number => {
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) {
            return asNumber(source[key], fallback);
        }
    }
    return fallback;
};

const readCounts = (source: Record<string, unknown>): Record<string, unknown> => asRecord(source.counts);

const formatMoney = (value: unknown): string =>
    asNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

const formatPercent = (value: number): string =>
    `${Math.round(Math.max(value, 0) * 100)}%`;

const formatDate = (value: unknown): string => {
    const raw = asString(value);
    if (!raw) return "-";

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toISOString().split("T")[0];
};

const getPackageMixRows = (payload: Record<string, unknown>): PackageMixRow[] => {
    const rawRows =
        (Array.isArray(payload.packageMix) && payload.packageMix) ||
        (Array.isArray(payload.topPackages) && payload.topPackages) ||
        [];

    return rawRows.map((item, index) => {
        const row = asRecord(item);

        return {
            id: asString(row.packageId) || asString(row._id) || asString(row.id) || String(index + 1),
            name: asString(row.name) || asString(row.packageName) || "Unknown package",
            soldCount: asNumber(row.soldCount || row.count),
            revenue: asNumber(row.revenue || row.totalRevenue),
        };
    });
};

const getMonthlyTrendRows = (payload: Record<string, unknown>): MonthlyTrendRow[] => {
    const rawRows =
        (Array.isArray(payload.yearlySubscriptionTrend) && payload.yearlySubscriptionTrend) ||
        (Array.isArray(payload.monthlySubscriptionTrend) && payload.monthlySubscriptionTrend) ||
        [];

    return rawRows.map((item, index) => {
        const row = asRecord(item);

        return {
            month: asNumber(row.month, index + 1),
            label: asString(row.label) || String(index + 1),
            totalSubscriptions: asNumber(row.totalSubscriptions || row.subscriptions || row.count),
        };
    });
};

const getCohortTrendRows = (payload: Record<string, unknown>): CohortTrendRow[] => {
    const rawRows =
        (Array.isArray(payload.newVsReturningTrend) && payload.newVsReturningTrend) ||
        (Array.isArray(payload.cohortTrend) && payload.cohortTrend) ||
        [];

    return rawRows.map((item, index) => {
        const row = asRecord(item);
        const newClients = asNumber(row.newClients || row.newMembers);
        const returningClients = asNumber(row.returningClients || row.renewedClients || row.oldClients);

        return {
            month: asNumber(row.month, index + 1),
            label: asString(row.label) || String(index + 1),
            newClients,
            returningClients,
            totalClients: asNumber(row.totalClients, newClients + returningClients),
        };
    });
};

const getFollowUpRows = (payload: Record<string, unknown>, key: "lostMembers" | "inactiveMembers"): FollowUpMemberRow[] => {
    const value = payload[key];
    const rawRows: unknown[] = Array.isArray(value) ? value : [];

    return rawRows.map((item, index) => {
        const row = asRecord(item);
        const member = asRecord(row.member);

        return {
            id: asString(row.subscriptionId) || asString(row.memberId) || asString(row._id) || asString(row.id) || String(index + 1),
            memberName:
                asString(row.memberName) ||
                [asString(member.firstName), asString(member.lastName)].filter(Boolean).join(" ") ||
                "-",
            packageName: asString(row.packageName) || asString(asRecord(row.package).name) || "-",
            expirationDate: formatDate(row.expirationDate || row.lastExpirationDate),
            daysOverdue: asNumber(row.daysOverdue),
            mobile: asString(row.mobile) || asString(member.mobile),
            email: asString(row.email) || asString(member.email),
        };
    });
};

const getRenewalConversion = (payload: Record<string, unknown>): RenewalConversion => {
    const conversion = asRecord(payload.renewalConversion);

    return {
        dueMembers: asNumber(conversion.dueMembers || conversion.due),
        renewedMembers: asNumber(conversion.renewedMembers || conversion.renewed),
        notRenewedMembers: asNumber(conversion.notRenewedMembers || conversion.notRenewed),
        conversionRate: asNumber(conversion.conversionRate || conversion.rate),
    };
};

const page = () => {
    const { user } = useAuth();
    const canViewActiveMembers = isSuperAdmin(user);
    const currentYear = new Date().getFullYear();
    const [trendYear, setTrendYear] = useState(currentYear);
    const [analyticsView, setAnalyticsView] = useState<AnalyticsView>("churn");
    const [followUpSearch, setFollowUpSearch] = useState("");
    const [renewStats, setRenewStats] = useState<Record<string, unknown> | null>(null);
    const [loadingStats, setLoadingStats] = useState(true);
    const [statsError, setStatsError] = useState("");

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoadingStats(true);
                setStatsError("");
                const response = await getRenewStats(DAYS_WINDOW, trendYear);
                setRenewStats(asRecord(response));
            } catch (err) {
                console.error("Failed to fetch renew stats", err);
                setStatsError("Failed to load renewal stats");
            } finally {
                setLoadingStats(false);
            }
        };

        fetchStats();
    }, [trendYear]);

    const packageMix = useMemo(() => (renewStats ? getPackageMixRows(renewStats) : []), [renewStats]);
    const monthlyTrend = useMemo(() => (renewStats ? getMonthlyTrendRows(renewStats) : []), [renewStats]);
    const cohortTrend = useMemo(() => (renewStats ? getCohortTrendRows(renewStats) : []), [renewStats]);
    const lostMembers = useMemo(() => (renewStats ? getFollowUpRows(renewStats, "lostMembers") : []), [renewStats]);
    const inactiveMembers = useMemo(() => (renewStats ? getFollowUpRows(renewStats, "inactiveMembers") : []), [renewStats]);
    const renewalConversion = useMemo(() => (renewStats ? getRenewalConversion(renewStats) : {
        dueMembers: 0,
        renewedMembers: 0,
        notRenewedMembers: 0,
        conversionRate: 0,
    }), [renewStats]);
    const yearOptions = useMemo(
        () => Array.from({ length: 6 }, (_, index) => currentYear - index),
        [currentYear]
    );
    const counts = renewStats ? readCounts(renewStats) : {};
    const activeMembers = canViewActiveMembers && renewStats
        ? readFirstNumber(
            counts,
            ["activeSubscriptionsThisMonth", "activeMembers", "totalActiveMembers", "members", "totalMembers"],
            0
        )
        : 0;
    const renewalsDue = renewStats
        ? readFirstNumber(
            counts,
            ["totalExpiredOrRenewWithinDays", "renewalsDue", "dueRenewals", "count", "total"],
            0
        )
        : 0;
    const lostMembersCount = renewStats
        ? readFirstNumber(counts, ["lostMembers", "churnedMembers"], lostMembers.length)
        : 0;
    const inactiveMembersCount = renewStats
        ? readFirstNumber(counts, ["inactiveMembers", "inactiveClients"], inactiveMembers.length)
        : 0;
    const subscriptionsStarted = renewStats
        ? readFirstNumber(counts, ["subscriptionsStartedThisMonth", "renewsThisMonth"], 0)
        : 0;
    const newClients = renewStats
        ? readFirstNumber(counts, ["newClientsThisMonth", "newMembersThisMonth", "newClients"], 0)
        : 0;
    const renewedClients = renewStats
        ? readFirstNumber(counts, ["renewedClientsThisMonth", "returningClientsThisMonth", "renewingClients"], 0)
        : 0;
    const subscriptionRevenue = renewStats
        ? readFirstNumber(counts, ["subscriptionRevenueThisMonth", "revenueThisMonth", "monthlyReturn"], 0)
        : 0;
    const clientsThisMonth = renewStats
        ? readFirstNumber(counts, ["clientsThisMonth", "monthlyClients"], newClients + renewedClients)
        : 0;
    const newClientShare = clientsThisMonth > 0 ? newClients / clientsThisMonth : 0;
    const renewalShare = clientsThisMonth > 0 ? renewedClients / clientsThisMonth : 0;
    const topPackageSoldCount = packageMix[0]?.soldCount || 0;
    const annualSubscriptions = monthlyTrend.reduce((total, row) => total + row.totalSubscriptions, 0);
    const annualNewClients = cohortTrend.reduce((total, row) => total + row.newClients, 0);
    const annualReturningClients = cohortTrend.reduce((total, row) => total + row.returningClients, 0);
    const maxCohortClients = Math.max(...cohortTrend.map((row) => row.totalClients), 1);
    const selectedFollowUpRows = analyticsView === "inactive" ? inactiveMembers : lostMembers;
    const normalizedFollowUpSearch = followUpSearch.trim().toLowerCase();
    const filteredFollowUpRows = normalizedFollowUpSearch
        ? selectedFollowUpRows.filter((row) =>
            [row.memberName, row.packageName, row.mobile, row.email].some((value) =>
                value.toLowerCase().includes(normalizedFollowUpSearch)
            )
        )
        : selectedFollowUpRows;
    const maxMonthlySubscriptions = Math.max(...monthlyTrend.map((row) => row.totalSubscriptions), 1);
    const chartWidth = 720;
    const chartHeight = 260;
    const chartPadding = 34;
    const chartBottom = chartHeight - chartPadding;
    const plotWidth = chartWidth - chartPadding * 2;
    const plotHeight = chartHeight - chartPadding * 2;
    const trendPoints = monthlyTrend.map((row, index) => {
        const x = chartPadding + (monthlyTrend.length > 1 ? (index * plotWidth) / (monthlyTrend.length - 1) : 0);
        const y = chartBottom - (row.totalSubscriptions / maxMonthlySubscriptions) * plotHeight;

        return { ...row, x, y };
    });
    const trendPath = trendPoints.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
    const areaPath = trendPoints.length
        ? `${trendPath} L ${trendPoints[trendPoints.length - 1].x} ${chartBottom} L ${trendPoints[0].x} ${chartBottom} Z`
        : "";
    const sessionsMadeToday = renewStats
        ? readFirstNumber(renewStats, ["sessionsMadeToday", "todaySessions", "sessionsToday"], 0)
        : 0;

    const stats = [
        canViewActiveMembers ? {
            label: "Active Members",
            value: loadingStats ? "..." : activeMembers.toLocaleString(),
            detail: "Active subscriptions this month",
            icon: Users,
            accent: "bg-[#ac120c]",
        } : null,
        {
            label: "Renewals Due",
            value: loadingStats ? "..." : renewalsDue.toLocaleString(),
            detail: `Next ${DAYS_WINDOW} days`,
            icon: CalendarClock,
            accent: "bg-amber-500",
            href: `/subscriptions/all?view=renewalsDue&days=${DAYS_WINDOW}`,
        },
        {
            label: "Sessions Made Today",
            value: loadingStats ? "..." : sessionsMadeToday.toLocaleString(),
            detail: "Completed today",
            icon: Dumbbell,
            accent: "bg-slate-900",
        },
    ].filter((item): item is NonNullable<typeof item> => Boolean(item));

    const subscriptionAnalytics = [
        {
            label: "Subscriptions Sold",
            value: loadingStats ? "..." : subscriptionsStarted.toLocaleString(),
            detail: "Started this month",
            icon: TrendingUp,
            accent: "bg-[#ac120c]",
        },
        {
            label: "New Clients",
            value: loadingStats ? "..." : newClients.toLocaleString(),
            detail: `${formatPercent(newClientShare)} of monthly clients`,
            icon: UserPlus,
            accent: "bg-emerald-600",
        },
        {
            label: "Returning Clients",
            value: loadingStats ? "..." : renewedClients.toLocaleString(),
            detail: `${formatPercent(renewalShare)} renewal share`,
            icon: RefreshCcw,
            accent: "bg-indigo-600",
        },
        {
            label: "Subscription Revenue",
            value: loadingStats ? "..." : formatMoney(subscriptionRevenue),
            detail: "Collected from new starts",
            icon: DollarSign,
            accent: "bg-gray-950",
        },
    ];

    const analyticsTabs = [
        {
            id: "churn" as const,
            label: "Churn",
            value: lostMembersCount.toLocaleString(),
            detail: "Lost in last 30 days",
            icon: UserX,
        },
        {
            id: "conversion" as const,
            label: "Conversion",
            value: `${renewalConversion.conversionRate}%`,
            detail: `${renewalConversion.renewedMembers}/${renewalConversion.dueMembers} renewed`,
            icon: Percent,
        },
        {
            id: "cohorts" as const,
            label: "New vs Returning",
            value: (annualNewClients + annualReturningClients).toLocaleString(),
            detail: `${trendYear} client mix`,
            icon: UserCheck,
        },
        {
            id: "inactive" as const,
            label: "Inactive",
            value: inactiveMembersCount.toLocaleString(),
            detail: "Need follow-up",
            icon: Users,
        },
    ];

    return (
        <div className="min-h-full bg-gray-50 text-gray-950">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
                <header className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
                    <div>
                        <p className="text-sm font-medium uppercase tracking-wide text-[#ac120c]">Dashboard</p>
                        <h1 className="mt-1 text-2xl font-bold tracking-tight md:text-3xl">Membership Overview</h1>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Monitor member activity, renewal risk, and daily gym performance from one place.
                        </p>
                    </div>

                </header>

                <section className={`grid grid-cols-1 gap-4 ${canViewActiveMembers ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                    {stats.map((item) => {
                        const Icon = item.icon;
                        const cardContent = (
                            <>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">{item.label}</p>
                                        <p className="mt-3 text-3xl font-bold tracking-tight">{item.value}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {item.href && (
                                            <span className="rounded-lg border border-gray-200 p-2 text-gray-500 transition group-hover:border-[#ac120c] group-hover:text-[#ac120c]">
                                                <ArrowRight className="h-4 w-4" />
                                            </span>
                                        )}
                                        <div className={`rounded-lg ${item.accent} p-3 text-white`}>
                                            <Icon className="h-5 w-5" />
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-4 text-sm text-gray-500">{item.detail}</p>
                            </>
                        );

                        return item.href ? (
                            <Link
                                key={item.label}
                                to={item.href}
                                className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-[#ac120c] hover:shadow-md"
                            >
                                {cardContent}
                            </Link>
                        ) : (
                            <div key={item.label} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                                {cardContent}
                            </div>
                        );
                    })}
                </section>

                <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold">Monthly Subscription Trend</h2>
                            <p className="text-sm text-gray-500">Total subscriptions started per month.</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <p className="text-sm font-semibold text-gray-600">{annualSubscriptions.toLocaleString()} total</p>
                            <select
                                value={trendYear}
                                onChange={(event) => setTrendYear(Number(event.target.value))}
                                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900"
                            >
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="p-5">
                        {loadingStats ? (
                            <div className="flex h-72 items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-500">
                                Loading trend...
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <svg
                                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                    className="h-72 min-w-[720px] rounded-lg bg-gray-50"
                                    role="img"
                                    aria-label={`Monthly total subscriptions trend for ${trendYear}`}
                                >
                                    {[0.25, 0.5, 0.75, 1].map((ratio) => {
                                        const y = chartBottom - ratio * plotHeight;

                                        return (
                                            <line
                                                key={ratio}
                                                x1={chartPadding}
                                                y1={y}
                                                x2={chartWidth - chartPadding}
                                                y2={y}
                                                stroke="#e5e7eb"
                                                strokeWidth="1"
                                            />
                                        );
                                    })}
                                    <line x1={chartPadding} y1={chartBottom} x2={chartWidth - chartPadding} y2={chartBottom} stroke="#cbd5e1" strokeWidth="1" />
                                    {areaPath && <path d={areaPath} fill="#ef4444" opacity="0.08" />}
                                    {trendPath && <path d={trendPath} fill="none" stroke="#ac120c" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />}
                                    {trendPoints.map((point) => (
                                        <g key={point.month}>
                                            <circle cx={point.x} cy={point.y} r="4" fill="#ac120c" />
                                            <text x={point.x} y={point.y - 10} textAnchor="middle" className="fill-gray-700 text-[11px] font-semibold">
                                                {point.totalSubscriptions}
                                            </text>
                                            <text x={point.x} y={chartHeight - 10} textAnchor="middle" className="fill-gray-500 text-[11px] font-medium">
                                                {point.label}
                                            </text>
                                        </g>
                                    ))}
                                </svg>
                            </div>
                        )}
                    </div>
                </section>

                <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-gray-200 p-5 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-lg font-bold">Retention Command Center</h2>
                            <p className="text-sm text-gray-500">Track lost members, renewal conversion, client mix, and inactive follow-up.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                            {analyticsTabs.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = analyticsView === tab.id;

                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setAnalyticsView(tab.id)}
                                        className={`rounded-lg border px-3 py-2 text-left transition ${isActive
                                            ? "border-[#ac120c] bg-rose-50 text-[#ac120c]"
                                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                            }`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold uppercase">{tab.label}</span>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <p className="mt-1 text-lg font-bold">{loadingStats ? "..." : tab.value}</p>
                                        <p className="text-xs text-gray-500">{tab.detail}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-5">
                        {analyticsView === "conversion" ? (
                            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
                                <div className="flex items-center justify-center">
                                    <div
                                        className="flex h-52 w-52 items-center justify-center rounded-full"
                                        style={{
                                            background: `conic-gradient(#ac120c ${renewalConversion.conversionRate * 3.6}deg, #e5e7eb 0deg)`,
                                        }}
                                    >
                                        <div className="flex h-36 w-36 flex-col items-center justify-center rounded-full bg-white text-center shadow-sm">
                                            <p className="text-4xl font-bold">{loadingStats ? "..." : `${renewalConversion.conversionRate}%`}</p>
                                            <p className="text-xs font-semibold uppercase text-gray-500">Renewed</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <div className="rounded-lg border border-gray-200 p-4">
                                        <p className="text-sm font-medium text-gray-500">Due This Month</p>
                                        <p className="mt-2 text-3xl font-bold">{loadingStats ? "..." : renewalConversion.dueMembers.toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 p-4">
                                        <p className="text-sm font-medium text-gray-500">Renewed</p>
                                        <p className="mt-2 text-3xl font-bold text-emerald-600">{loadingStats ? "..." : renewalConversion.renewedMembers.toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 p-4">
                                        <p className="text-sm font-medium text-gray-500">Not Renewed</p>
                                        <p className="mt-2 text-3xl font-bold text-[#ac120c]">{loadingStats ? "..." : renewalConversion.notRenewedMembers.toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>
                        ) : analyticsView === "cohorts" ? (
                            <div>
                                <div className="mb-4 flex flex-wrap items-center gap-4 text-sm">
                                    <span className="inline-flex items-center gap-2 font-semibold text-emerald-700">
                                        <span className="h-3 w-3 rounded-sm bg-emerald-500" />
                                        {annualNewClients.toLocaleString()} new
                                    </span>
                                    <span className="inline-flex items-center gap-2 font-semibold text-indigo-700">
                                        <span className="h-3 w-3 rounded-sm bg-indigo-500" />
                                        {annualReturningClients.toLocaleString()} returning
                                    </span>
                                </div>
                                <div className="grid h-72 grid-cols-12 items-end gap-2 rounded-lg bg-gray-50 p-4">
                                    {cohortTrend.map((row) => {
                                        const totalHeight = Math.max((row.totalClients / maxCohortClients) * 100, row.totalClients > 0 ? 8 : 0);
                                        const newHeight = row.totalClients ? (row.newClients / row.totalClients) * totalHeight : 0;
                                        const returningHeight = row.totalClients ? (row.returningClients / row.totalClients) * totalHeight : 0;

                                        return (
                                            <div key={row.month} className="flex h-full min-w-0 flex-col items-center justify-end gap-2">
                                                <div className="flex w-full flex-1 items-end justify-center">
                                                    <div className="flex h-full w-full max-w-8 flex-col justify-end overflow-hidden rounded-t-md bg-gray-200">
                                                        <div
                                                            className="bg-indigo-500"
                                                            style={{ height: `${returningHeight}%` }}
                                                            title={`${row.returningClients} returning clients`}
                                                        />
                                                        <div
                                                            className="bg-emerald-500"
                                                            style={{ height: `${newHeight}%` }}
                                                            title={`${row.newClients} new clients`}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-xs font-bold text-gray-800">{row.totalClients}</p>
                                                    <p className="text-[11px] font-medium text-gray-500">{row.label}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <h3 className="font-bold">{analyticsView === "inactive" ? "Inactive Members" : "Lost Members"}</h3>
                                        <p className="text-sm text-gray-500">
                                            {analyticsView === "inactive"
                                                ? "Members without an active subscription."
                                                : "Members whose latest subscription expired and did not renew."}
                                        </p>
                                    </div>
                                    <label className="relative block sm:w-72">
                                        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                        <input
                                            type="search"
                                            value={followUpSearch}
                                            onChange={(event) => setFollowUpSearch(event.target.value)}
                                            placeholder="Search follow-up..."
                                            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
                                        />
                                    </label>
                                </div>
                                {loadingStats ? (
                                    <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">Loading members...</div>
                                ) : filteredFollowUpRows.length > 0 ? (
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        {filteredFollowUpRows.map((member) => (
                                            <div key={member.id} className="rounded-lg border border-gray-200 p-4">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <p className="font-bold text-gray-950">{member.memberName}</p>
                                                        <p className="mt-1 text-sm text-gray-500">{member.packageName}</p>
                                                    </div>
                                                    <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-[#ac120c]">
                                                        {member.daysOverdue > 0 ? `${member.daysOverdue}d late` : "Inactive"}
                                                    </span>
                                                </div>
                                                <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-gray-600 sm:grid-cols-2">
                                                    <p>Expired: <span className="font-semibold text-gray-900">{member.expirationDate}</span></p>
                                                    <p>Phone: <span className="font-semibold text-gray-900">{member.mobile || "-"}</span></p>
                                                    <p className="sm:col-span-2">Email: <span className="font-semibold text-gray-900">{member.email || "-"}</span></p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">No members found.</div>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="border-b border-gray-200 p-5">
                            <h2 className="text-lg font-bold">Subscription Growth</h2>
                            <p className="text-sm text-gray-500">New sales, new clients, and renewal momentum for this month.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
                            {subscriptionAnalytics.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <div key={item.label} className="rounded-lg border border-gray-200 p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-medium text-gray-500">{item.label}</p>
                                                <p className="mt-2 text-2xl font-bold tracking-tight">{item.value}</p>
                                            </div>
                                            <div className={`rounded-lg ${item.accent} p-2.5 text-white`}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                        </div>
                                        <p className="mt-3 text-sm text-gray-500">{item.detail}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="flex items-center justify-between border-b border-gray-200 p-5">
                            <div>
                                <h2 className="text-lg font-bold">Top Packages</h2>
                                <p className="text-sm text-gray-500">Best sellers this month.</p>
                            </div>
                            <Trophy className="h-5 w-5 text-amber-500" />
                        </div>
                        <div className="space-y-4 p-5">
                            {loadingStats ? (
                                <p className="text-sm text-gray-500">Loading package mix...</p>
                            ) : packageMix.length > 0 ? (
                                packageMix.map((item) => {
                                    const width = topPackageSoldCount > 0 ? Math.max((item.soldCount / topPackageSoldCount) * 100, 8) : 0;

                                    return (
                                        <div key={item.id}>
                                            <div className="flex items-center justify-between gap-3 text-sm">
                                                <p className="truncate font-semibold text-gray-950">{item.name}</p>
                                                <p className="shrink-0 text-gray-500">{item.soldCount.toLocaleString()} sold</p>
                                            </div>
                                            <div className="mt-2 h-2 rounded-full bg-gray-100">
                                                <div className="h-2 rounded-full bg-[#ac120c]" style={{ width: `${width}%` }} />
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">{formatMoney(item.revenue)} revenue</p>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-gray-500">No subscription sales yet this month.</p>
                            )}
                        </div>
                    </div>
                </section>

                {statsError && <p className="text-sm text-red-600">{statsError}</p>}
            </div>
        </div>
    );
};

export default page;
